import { MouseEvent } from "react"
import classNames from "classnames"
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import {
   CellRendererArgs,
   CreateTextCellRendererProps,
   InputType,
   TextCellRendererProps,
} from "@/components/shared/Table/types"
import TextInput from "@/components/shared/TextInput"
import { safeToString } from "@/utils/index"
import { isUndefined } from "@/utils/lodash"
import "./index.scss"

interface BaseCellRendererProps<T> extends TextCellRendererProps<T> {
   disabled?: boolean
   icon?: {
      name: string
      color?: IconColor
      size?: string
      onClick?: (id: string) => void
      row?: T & { id: string }
      showOnlyOnView?: boolean
   }
   cellTextTestId?: string
   onTextClick?: () => void
}

function BaseCellRenderer<T>(props: BaseCellRendererProps<T>) {
   const {
      inputType = InputType.TEXT,
      field,
      value,
      isEditing,
      onInputChange,
      placeholder,
      hasError = false,
      disabled = false,
      icon,
      onBulkPaste,
      onTextClick,
   } = props

   if (!isEditing) {
      return (
         <div
            className={classNames("table__cell-content", {
               "table__cell-content--with-icon": icon,
            })}
         >
            {icon && icon?.showOnlyOnView && (
               <Icon
                  name={icon.name as IconProps["name"]}
                  color={icon.color || IconColor.HONOLULU_BLUE}
                  size={icon.size || "20"}
               />
            )}
            <div
               className={classNames("table__cell-text", {
                  "table__cell-text--clickable": !!onTextClick,
               })}
               onClick={onTextClick}
               {...(props.cellTextTestId && {
                  "data-testid": props.cellTextTestId,
               })}
            >
               {safeToString(value)}
            </div>
         </div>
      )
   }
   const handleIconClick =
      icon?.row && !isUndefined(icon.onClick)
         ? (() => {
              const row = icon.row
              return (e: MouseEvent) => {
                 e.stopPropagation()
                 icon.onClick?.(row.id)
              }
           })()
         : undefined

   return (
      <div
         className={classNames("table__cell-content", {
            "table__cell-content--readonly": disabled,
            "table__cell-content--with-clickable-icon": icon,
         })}
      >
         <TextInput
            type={inputType}
            value={safeToString(value)}
            placeholder={placeholder || ""}
            disabled={disabled}
            error={hasError}
            onChange={(e) => onInputChange(field, e.target.value)}
            onBulkPaste={onBulkPaste}
         />

         {icon && (
            <div className="table__cell-icon-container">
               <Icon
                  name={icon.name as IconProps["name"]}
                  color={icon.color || IconColor.HONOLULU_BLUE}
                  onClick={handleIconClick}
                  size={icon.size || "20"}
               />
            </div>
         )}
      </div>
   )
}

export function InputCellRenderer<T extends object>(
   props: CreateTextCellRendererProps<T>
) {
   const {
      inputType = InputType.TEXT,
      field,
      placeholder,
      disabled = false,
      iconName,
      iconColor = IconColor.HONOLULU_BLUE,
      iconSize = "17",
      showOnlyOnView,
      onIconClick,
      onTextClick,
   } = props

   return function TextCellRendererWrapper({
      value,
      row,
      isEditing,
      onInputChange,
      onBulkPaste,
      hasError,
      cellTextTestId,
   }: CellRendererArgs<T>) {
      return BaseCellRenderer({
         inputType,
         field,
         value,
         isEditing,
         onInputChange,
         onBulkPaste,
         hasError,
         placeholder,
         disabled,
         cellTextTestId,
         onTextClick: onTextClick && (() => onTextClick(row)),
         ...(iconName &&
            onIconClick && {
               icon: {
                  name: iconName,
                  color: iconColor,
                  size: iconSize,
                  showOnlyOnView,
                  onClick: onIconClick,
                  row: "id" in row ? (row as T & { id: string }) : undefined,
               },
            }),
      })
   }
}
